/**
 * Phase 4: Relationships
 * AI-assisted table relationship discovery
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { 
  Link2, Loader2, Check, Wand2, Trash2, Plus, 
  ChevronRight, CheckCircle2, AlertCircle, Sparkles, List
} from 'lucide-react';
import { 
  getTableRelationships, saveTableRelationship, deleteTableRelationship,
  discoverRelationships, bulkSaveRelationships, getSchemaStructure
} from '../../services/api';
import RelationshipWizard from './RelationshipWizard';
import ComplexMappings from './ComplexMappings';

const Phase4Relationships = ({ onComplete, isAdmin }) => {
  const [activeMode, setActiveMode] = useState('wizard'); // 'wizard' or 'manual'
  const [loading, setLoading] = useState(true);
  const [relationships, setRelationships] = useState([]);
  const [schemaStructure, setSchemaStructure] = useState({});
  const [discovering, setDiscovering] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [savingSuggestions, setSavingSuggestions] = useState(false);
  
  // New relationship form
  const [newRel, setNewRel] = useState({
    source_server: '', source_database: '', source_table: '', source_column: '',
    target_server: '', target_database: '', target_table: '', target_column: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rels, schema] = await Promise.all([
        getTableRelationships(),
        getSchemaStructure()
      ]);
      setRelationships(rels.relationships || []);
      setSchemaStructure(schema.structure || {});
      
      // Don't calculate progress here - backend calculates from actual data
      // Just notify parent that loading is done
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load relationships');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverRelationships = async () => {
    setDiscovering(true);
    try {
      console.log('[Quick Discover] Starting AI relationship discovery...');
      const result = await discoverRelationships();
      console.log('[Quick Discover] Result:', result);
      
      if (result.suggestions && result.suggestions.length > 0) {
        setAiSuggestions(result.suggestions);
        toast.success(`AI found ${result.suggestions.length} potential relationships`);
      } else if (result.error) {
        toast.error(`Discovery failed: ${result.error}`);
      } else {
        // More informative message
        toast.info('No new relationships discovered. All obvious relationships may already be defined.', { duration: 5000 });
      }
    } catch (error) {
      console.error('[Quick Discover] Error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to discover relationships: ${errorMsg}`);
    } finally {
      setDiscovering(false);
    }
  };

  const handleAcceptSuggestion = (suggestion, index) => {
    // Mark as accepted
    setAiSuggestions(prev => prev.map((s, i) => 
      i === index ? { ...s, accepted: true } : s
    ));
  };

  const handleRejectSuggestion = (index) => {
    setAiSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveAcceptedSuggestions = async () => {
    const accepted = aiSuggestions.filter(s => s.accepted);
    if (accepted.length === 0) {
      toast.error('No suggestions accepted');
      return;
    }

    setSavingSuggestions(true);
    try {
      await bulkSaveRelationships(accepted);
      toast.success(`Saved ${accepted.length} relationships`);
      setAiSuggestions([]);
      await fetchData();
    } catch (error) {
      toast.error('Failed to save relationships');
    } finally {
      setSavingSuggestions(false);
    }
  };

  const handleSaveNewRelationship = async () => {
    if (!newRel.source_table || !newRel.target_table || !newRel.source_column || !newRel.target_column) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await saveTableRelationship(newRel);
      toast.success('Relationship saved');
      setNewRel({
        source_server: '', source_database: '', source_table: '', source_column: '',
        target_server: '', target_database: '', target_table: '', target_column: '',
        description: ''
      });
      await fetchData();
    } catch (error) {
      toast.error('Failed to save relationship');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRelationship = async (relId) => {
    if (!window.confirm('Delete this relationship?')) return;
    
    try {
      await deleteTableRelationship(relId);
      toast.success('Relationship deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete relationship');
    }
  };

  const servers = Object.keys(schemaStructure);

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-purple-600" />
            Phase 4: Table Relationships
            <span className="ml-2 text-sm font-normal text-gray-400">
              {relationships.length} defined
            </span>
          </CardTitle>
          
          {/* Mode Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <Button
              variant={activeMode === 'wizard' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveMode('wizard')}
              className={activeMode === 'wizard' ? 'bg-purple-500 hover:bg-purple-600' : ''}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              AI Wizard
            </Button>
            <Button
              variant={activeMode === 'manual' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveMode('manual')}
              className={activeMode === 'manual' ? 'bg-gray-700 hover:bg-gray-800' : ''}
            >
              <List className="w-4 h-4 mr-1" />
              Manual
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {activeMode === 'wizard' ? (
          /* AI Wizard Mode */
          <RelationshipWizard onComplete={onComplete} isAdmin={isAdmin} />
        ) : (
          /* Manual Mode */
          <>
            <p className="text-sm text-gray-500">
              Define how tables connect across servers/databases. Supports cross-server relationships.
            </p>

            {/* AI Quick Discover Button */}
            {isAdmin && (
              <div className="flex justify-end">
                <Button 
                  onClick={handleDiscoverRelationships} 
                  disabled={discovering}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  {discovering ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Quick AI Discover</>
                  )}
                </Button>
              </div>
            )}

            {/* AI Suggestions */}
            {aiSuggestions.length > 0 && (
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-purple-800 flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                AI Discovered Relationships
              </h4>
              <Button 
                size="sm" 
                onClick={handleSaveAcceptedSuggestions}
                disabled={savingSuggestions || !aiSuggestions.some(s => s.accepted)}
              >
                {savingSuggestions ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Save Accepted ({aiSuggestions.filter(s => s.accepted).length})
              </Button>
            </div>
            
            {aiSuggestions.map((suggestion, idx) => (
              <div 
                key={idx} 
                className={`p-3 bg-white rounded border ${suggestion.accepted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium">{suggestion.source_table}.{suggestion.source_column}</span>
                    <ChevronRight className="w-4 h-4 inline mx-2 text-gray-400" />
                    <span className="font-medium">{suggestion.target_table}.{suggestion.target_column}</span>
                    {suggestion.confidence && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({Math.round(suggestion.confidence * 100)}% confidence)
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {!suggestion.accepted ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleAcceptSuggestion(suggestion, idx)}>
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRejectSuggestion(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </>
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </div>
                {suggestion.description && (
                  <p className="text-xs text-gray-500 mt-1">{suggestion.description}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Existing Relationships */}
        {relationships.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700">Defined Relationships</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {relationships.map((rel) => (
                <div key={rel.id} className="p-3 bg-gray-50 rounded border border-gray-200 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium text-blue-600">{rel.source_table}.{rel.source_column}</span>
                    <ChevronRight className="w-4 h-4 inline mx-2 text-gray-400" />
                    <span className="font-medium text-green-600">{rel.target_table}.{rel.target_column}</span>
                    {rel.description && (
                      <span className="ml-2 text-gray-400 text-xs">({rel.description})</span>
                    )}
                  </div>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteRelationship(rel.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add New Relationship */}
        {isAdmin && servers.length > 0 && (
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Relationship Manually
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Source */}
              <div className="space-y-2 p-3 bg-blue-50 rounded">
                <Label className="text-xs text-blue-700">Source Table</Label>
                <Select value={newRel.source_server} onValueChange={v => setNewRel({...newRel, source_server: v, source_database: '', source_table: '', source_column: ''})}>
                  <SelectTrigger><SelectValue placeholder="Server" /></SelectTrigger>
                  <SelectContent>
                    {servers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {newRel.source_server && (
                  <Select value={newRel.source_database} onValueChange={v => setNewRel({...newRel, source_database: v, source_table: '', source_column: ''})}>
                    <SelectTrigger><SelectValue placeholder="Database" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(schemaStructure[newRel.source_server] || {}).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {newRel.source_database && (
                  <Select value={newRel.source_table} onValueChange={v => setNewRel({...newRel, source_table: v, source_column: ''})}>
                    <SelectTrigger><SelectValue placeholder="Table" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(schemaStructure[newRel.source_server]?.[newRel.source_database] || {}).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {newRel.source_table && (
                  <Select value={newRel.source_column} onValueChange={v => setNewRel({...newRel, source_column: v})}>
                    <SelectTrigger><SelectValue placeholder="Column" /></SelectTrigger>
                    <SelectContent>
                      {(schemaStructure[newRel.source_server]?.[newRel.source_database]?.[newRel.source_table] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Target */}
              <div className="space-y-2 p-3 bg-green-50 rounded">
                <Label className="text-xs text-green-700">Target Table</Label>
                <Select value={newRel.target_server} onValueChange={v => setNewRel({...newRel, target_server: v, target_database: '', target_table: '', target_column: ''})}>
                  <SelectTrigger><SelectValue placeholder="Server" /></SelectTrigger>
                  <SelectContent>
                    {servers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {newRel.target_server && (
                  <Select value={newRel.target_database} onValueChange={v => setNewRel({...newRel, target_database: v, target_table: '', target_column: ''})}>
                    <SelectTrigger><SelectValue placeholder="Database" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(schemaStructure[newRel.target_server] || {}).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {newRel.target_database && (
                  <Select value={newRel.target_table} onValueChange={v => setNewRel({...newRel, target_table: v, target_column: ''})}>
                    <SelectTrigger><SelectValue placeholder="Table" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(schemaStructure[newRel.target_server]?.[newRel.target_database] || {}).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {newRel.target_table && (
                  <Select value={newRel.target_column} onValueChange={v => setNewRel({...newRel, target_column: v})}>
                    <SelectTrigger><SelectValue placeholder="Column" /></SelectTrigger>
                    <SelectContent>
                      {(schemaStructure[newRel.target_server]?.[newRel.target_database]?.[newRel.target_table] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <Input
              value={newRel.description}
              onChange={e => setNewRel({...newRel, description: e.target.value})}
              placeholder="Description (optional): e.g., User's primary account"
            />

            <Button onClick={handleSaveNewRelationship} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Relationship
            </Button>
          </div>
        )}

        {servers.length === 0 && (
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No schema data available. Complete Phase 3 first.</p>
          </div>
        )}
          </>
        )}
        
        {/* Complex/Conditional Mappings - always visible */}
        <div className="mt-6 pt-6 border-t">
          <ComplexMappings database={Object.keys(schemaStructure)[0]} />
        </div>
      </CardContent>
    </Card>
  );
};

export default Phase4Relationships;
